import { formatCard, formatMoney } from '@app/shared'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { orgHref } from '../lib/public-router'
import { holdsThePayment, returnedCheckout } from '../lib/returned-checkout'
import { useTRPC } from '../lib/trpc'
import { continueLabel } from '../lib/two-step-payment'
import {
  Button,
  Card,
  Figure,
  Notice,
  PaymentFlow,
  PaymentStep,
  ReturnedCheckoutNotice,
  useCheckoutConfirmation,
  useReturnedCheckout,
} from '../ui'
import { PublicNotFound } from '../ui/ErrorPage'
import { PublicShell } from '../ui/PublicShell'
import './gift.css'

/** What a new card does once it is on the gift, said the same everywhere. */
const NEXT_CHARGE =
  'Anything this gift still owes is charged to the new card next; later gifts on their usual day.'

/**
 * The page a gift's change-card link opens: the gift, the card it is charged
 * to now, and a new card saved in its place. No login: the `?token=` is the
 * whole authority, read on the page of the org the gift belongs to, and it
 * reaches only that gift's own card.
 *
 * Saving charges nothing at that moment. The new card then pays what the gift
 * still owes — a period its old card was declined for is tried on the new one
 * straight away — and every later gift on its usual day.
 */
export function ChangeCardPage({
  orgId,
  onLogin,
}: {
  orgId: string
  onLogin?: () => void
}) {
  const trpc = useTRPC()
  const params = new URLSearchParams(window.location.search)
  const token = params.get('token') ?? ''
  const link = { shortId: orgId, token }
  /* A checkout named in the address is a claim until the server reads it: the
     address is written there when a card completes in place, and a reload or
     a bookmark keeps it long after. */
  const [checkoutId, showCheckout] = useReturnedCheckout()

  const org = useQuery(trpc.donation.org.queryOptions({ shortId: orgId }))
  const gift = useQuery({
    ...trpc.giftCard.view.queryOptions(link),
    enabled: token.length > 0,
    retry: false,
    refetchOnWindowFocus: false,
  })
  const outcome = useQuery({
    ...trpc.donation.checkoutOutcome.queryOptions({
      shortId: orgId,
      checkoutId: checkoutId ?? '',
    }),
    enabled: checkoutId !== null,
    retry: false,
  })
  const checkout = useMutation(trpc.giftCard.checkout.mutationOptions())
  const confirmation = useCheckoutConfirmation({
    onComplete: (id) => showCheckout(id),
  })

  /* A setup checkout the server reads as complete is a card Stripe saved. */
  const returned = returnedCheckout({
    checkoutId,
    status: outcome.data?.status,
    isError: outcome.isError,
  })
  const saved = returned === 'paid'

  /* The card step goes once it has done its work, so the sentence saying what
     happened takes the keyboard in its place. */
  const done = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (saved) done.current?.focus()
  }, [saved])

  if (org.isLoading || org.error || !org.data) {
    return (
      <PublicShell name={null} orgId={null} onLogin={onLogin}>
        {org.isLoading ? (
          <div className="gift">
            <Card className="gift__card">
              <Notice>Loading…</Notice>
            </Card>
          </div>
        ) : (
          <PublicNotFound />
        )}
      </PublicShell>
    )
  }

  const o = org.data
  const view = gift.data ?? null
  const each = view?.cadence === 'annual' ? 'a year' : 'a month'

  return (
    <PublicShell
      name={o.name}
      nameHe={o.nameHe}
      language={o.publicLanguage}
      orgId={o.shortId}
      acceptsDonations={o.acceptsDonations}
      contact={o}
      onLogin={onLogin}
    >
      <div className="gift">
        <Card className="gift__card">
          <h1 className="gift__title">The card for your gift</h1>
          {(token && gift.isLoading) || returned === 'checking' ? (
            <p className="gift__muted">Loading…</p>
          ) : !token || gift.isError || !view ? (
            <Notice>
              {gift.error?.message ??
                'This link is not valid. Please use the link in your receipt, or contact the office.'}
            </Notice>
          ) : saved ? (
            <>
              <div ref={done} tabIndex={-1}>
                <Notice tone="info">
                  Your new card is saved. {NEXT_CHARGE}
                </Notice>
              </div>
              <a href={orgHref(o.shortId, '')}>Back to {o.name}</a>
            </>
          ) : (
            <PaymentFlow>
              <ReturnedCheckoutNotice
                state={returned}
                unpaid="The new card was not saved. Nothing has changed on the gift."
                expired="The new card was not saved. Nothing has changed on the gift."
                processing="Your new card is being confirmed. Nothing more is needed; the gift uses it once it is."
              />
              <Figure
                amount={`${formatMoney(view.rateCents)} ${each}`}
                label={view.toward ? `Toward ${view.toward}.` : 'Your gift.'}
              />
              <p className="gift__muted">
                {view.card
                  ? `Charged to ${formatCard(view.card)}.`
                  : 'The card on this gift could not be read just now.'}
              </p>
              {view.stoppedTrying && (
                <Notice>
                  We stopped trying this card after it was declined. A new card
                  starts the gift again.
                </Notice>
              )}
              {checkout.error && (
                <Notice tone="error">{checkout.error.message}</Notice>
              )}
              <Button
                disabled={checkout.isPending || holdsThePayment(returned)}
                onClick={() => {
                  showCheckout(null)
                  checkout.mutate(link)
                }}
              >
                {checkout.isPending
                  ? continueLabel(true)
                  : 'Use a different card'}
              </Button>
              <a href={orgHref(o.shortId, '')}>Back to {o.name}</a>
              {checkout.data && (
                <PaymentStep
                  orgId={o.shortId}
                  card={checkout.data.card}
                  what={
                    view.toward
                      ? `Your gift toward ${view.toward}`
                      : 'Your gift'
                  }
                  amount={formatMoney(view.rateCents)}
                  amountNote={each}
                  payLabel="Save card"
                  fine={`Nothing is charged as you save it. ${NEXT_CHARGE}`}
                  confirmation={confirmation}
                  onBack={() => {
                    checkout.reset()
                    confirmation.reset()
                    showCheckout(null)
                  }}
                />
              )}
            </PaymentFlow>
          )}
        </Card>
      </div>
    </PublicShell>
  )
}
